import type {
  ApiMonitorDefinition,
  ApiRequestClassifier,
  ApiUrlPattern,
  MonitoringMetricDefinition,
  MonitoringStatus,
  MonitoringThreshold,
  MonitoringSource,
  PodMonitoringSchema,
} from "./types";

// Schema builders used to define monitoring config in a consistent shape.
// Pass-through helper that keeps metric definitions strongly typed at call sites.
export function createMetricDefinition(
  metric: MonitoringMetricDefinition
): MonitoringMetricDefinition {
  return metric;
}

type MetricBaseInput = Pick<MonitoringMetricDefinition, "id" | "name" | "logic" | "unit"> &
  Pick<MonitoringMetricDefinition, "description" | "tags" | "meta">;

type MetricWithThresholdsInput = MetricBaseInput & {
  status?: MonitoringStatus;
  source?: MonitoringSource;
  thresholds?: MonitoringThreshold[];
};

function createMetricWithDefaults(
  defaults: Pick<MonitoringMetricDefinition, "category" | "source" | "status" | "thresholds">,
  metric: MetricWithThresholdsInput
): MonitoringMetricDefinition {
  return createMetricDefinition({
    ...metric,
    category: defaults.category,
    source: metric.source ?? defaults.source,
    status: metric.status ?? defaults.status,
    thresholds: metric.thresholds ?? defaults.thresholds,
  });
}

export function createAvailabilityMetric(
  metric: MetricWithThresholdsInput
): MonitoringMetricDefinition {
  return createMetricWithDefaults(
    {
      category: "availability",
      source: "new_relic",
      status: "planned",
      thresholds: [],
    },
    metric
  );
}

export function createUxMetric(metric: MetricWithThresholdsInput): MonitoringMetricDefinition {
  return createMetricWithDefaults(
    {
      category: "ux",
      source: "manual",
      status: "active",
      thresholds: [],
    },
    metric
  );
}

export const deadClickMetric = createUxMetric({
  id: "ux.dead_clicks",
  name: "Dead clicks",
  logic: "clicks without visible response or navigation",
  unit: "count",
});

export const rageClickMetric = createUxMetric({
  id: "ux.rage_clicks",
  name: "Rage clicks",
  logic: "rapid repeated clicks on the same element",
  unit: "count",
});

export const redirectionSuccessMetric = createUxMetric({
  id: "ux.redirection_success",
  name: "Redirection success",
  logic: "success signal for navigation or redirect attempts triggered by link CTAs",
  unit: "attempt",
});

export const clickResponseTimeMetric = createUxMetric({
  id: "ux.click_response_time",
  name: "Click response time",
  source: "custom_timer",
  logic: "timer from CTA click to first observable UI response",
  unit: "ms",
});

export function createBusinessMetric(
  metric: MetricWithThresholdsInput
): MonitoringMetricDefinition {
  return createMetricWithDefaults(
    {
      category: "business",
      source: "custom_timer",
      status: "active",
      thresholds: [],
    },
    metric
  );
}

export const componentRenderMetric = createBusinessMetric({
  id: "business.component_rendering_on_page",
  name: "Component rendering on page",
  logic:
    "component render success signal for critical business modules using configured root and content selectors",
  unit: "component",
});

// Builds the standard error-rate and response-time metrics for one API surface.
export function createApiMetricPair(config: {
  apiName: string;
  displayName: string;
  urlPatterns: ApiUrlPattern[];
  source?: MonitoringSource;
  errorRateThresholdPct?: number;
  responseTimeThresholdMs?: number;
  windowMinutes?: number;
  methods?: string[];
  classifyRequest?: ApiRequestClassifier;
}): { metrics: MonitoringMetricDefinition[]; apiMonitor: ApiMonitorDefinition } {
  const source = config.source ?? "new_relic";
  const windowMinutes = config.windowMinutes ?? 30;
  const errorMetricId = `api.${config.apiName}.error_rate`;
  const responseTimeMetricId = `api.${config.apiName}.response_time`;

  return {
    metrics: [
      createMetricDefinition({
        id: errorMetricId,
        name: `${config.displayName} error rate`,
        category: "api",
        source,
        unit: "request",
        status: "active",
        logic: "percentage(count(*), WHERE is_error = true) over request samples",
        thresholds: [
          {
            value: config.errorRateThresholdPct ?? 1,
            unit: "%",
            operator: "lte",
            windowMinutes,
            description: "Alert in New Relic on rolling error rate over request samples.",
          },
        ],
        meta: {
          api_name: config.apiName,
          api_group: config.apiName,
          metric_family: "api_error_rate",
        },
      }),
      createMetricDefinition({
        id: responseTimeMetricId,
        name: `${config.displayName} response time`,
        category: "api",
        source,
        unit: "ms",
        status: "active",
        logic: "percentile(metric_value, 90) over request duration samples",
        thresholds: [
          {
            value: config.responseTimeThresholdMs ?? 400,
            unit: "ms",
            operator: "lte",
            windowMinutes,
            percentile: 90,
            description: "Alert in New Relic on p90 response time over request samples.",
          },
        ],
        meta: {
          api_name: config.apiName,
          api_group: config.apiName,
          metric_family: "api_response_time",
        },
      }),
    ],
    apiMonitor: {
      apiName: config.apiName,
      errorMetricId,
      responseTimeMetricId,
      urlPatterns: config.urlPatterns,
      methods: config.methods,
      classifyRequest: config.classifyRequest,
    },
  };
}

// Pass-through helper that keeps whole pod schemas strongly typed at definition sites.
export function createPodMonitoringSchema(schema: PodMonitoringSchema): PodMonitoringSchema {
  return schema;
}
